import classNames from "classnames";
import {useFormContext} from "react-hook-form";

import {Card} from "@/components/card/Card.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {MultiServiceSelectCardField} from "@/components/form/fields/MultiServiceSelectCardField/MultiServiceSelectCardField.tsx";
import {PasswordField} from "@/components/form/fields/PasswordField/PasswordField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {TextAreaField} from "@/components/form/fields/TextAreaField.tsx";
import {TimezoneSelectField} from "@/components/form/fields/TimezoneSelectField/TimezoneSelectField.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {RequiredFieldsNote} from "@/components/form/requiredFieldsNote/RequiredFieldsNote.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {Section} from "@/components/section/Section.tsx";
import {ToggleButton} from "@/components/toggleButton/ToggleButton.tsx";
import {MARKDOWN_GUIDE_URL, STATUS_PAGE_OVERVIEW_URL} from "@/lib/links.ts";
import {
  StatusPageTimeFormat,
  StatusPageTimeFormatDisplay,
  StatusPageVisibilityLevel,
  UptimeCalculationType,
} from "@/lib/types/statuspages.ts";
import {StatusPageSlugField} from "@/pages/statuspages/tabs/general/subComponents/StatusPageSlugField.tsx";
import {VisibilityField} from "@/pages/statuspages/tabs/general/subComponents/VisibilityField.tsx";

import styles from "./StatusPageGeneralTab.module.scss";

interface StatusPageGeneralTabV2Props {
  variant?: "create" | "edit";
  className?: string;
}

export const StatusPageGeneralTabV2 = ({variant = "create", className}: StatusPageGeneralTabV2Props) => {
  const {watch, setValue} = useFormContext();
  const visibilityLevel = watch("visibility_level");
  const uptimeCalculationType = watch("uptime_calculation_type");
  const allowDrillDown = watch("allow_drill_down");
  const {userHasFeature} = useCurrentUserContext();

  const isEdit = variant === "edit";
  const showSlug = visibilityLevel !== StatusPageVisibilityLevel.UPTIME_USERS;
  const showChecks =
    !isEdit &&
    [StatusPageVisibilityLevel.UPTIME_USERS, StatusPageVisibilityLevel.EXTERNAL_USERS].includes(visibilityLevel);
  const isPublic = visibilityLevel === StatusPageVisibilityLevel.PUBLIC;
  const isPrivate = visibilityLevel === StatusPageVisibilityLevel.EXTERNAL_USERS;
  const isUptimeUsers = visibilityLevel === StatusPageVisibilityLevel.UPTIME_USERS;
  const showAccessControls = !isUptimeUsers;

  const uptimeCalculationItems = [
    {id: UptimeCalculationType.BY_CHECKS, label: "Check-based"},
    {id: UptimeCalculationType.BY_INCIDENTS, label: "Incident-based"},
  ];

  const getAdvancedSettingsDescription = () => {
    if (isUptimeUsers) return "Configure Google Analytics";
    return "Access controls, custom domain, and analytics";
  };

  return (
    <div className={classNames("d-flex flex-column gap-4 mb-4", className)}>
      {!isEdit && (
        <div className={"pulseTextBodySmall"}>
          <span>
            Set up your Status Page and visibility. You can customize the design, add components, and configure
            subscribers after creation.{" "}
          </span>
          <a href={STATUS_PAGE_OVERVIEW_URL}>View our documentation</a>
        </div>
      )}
      <RequiredFieldsNote spacing={"none"} />

      <InputField name={"name"} required={true} label={"Page Name"} autoFocus />

      <VisibilityField name="visibility_level" required />

      {showSlug && <StatusPageSlugField />}

      {showChecks && <MultiServiceSelectCardField name="initial_services" />}

      {!isEdit && <Section title={"optional"} className={"py-2"} />}

      <TextAreaField
        name={"description"}
        label={"Status Page Description"}
        supportLink={MARKDOWN_GUIDE_URL}
        supportLinkText={"Markdown supported"}
        supportLinkPosition={"before"}
        helpText={". Displayed at the top of your Status Page."}
      />
      <div>
        <div className={"d-flex gap-2"}>
          <TimezoneSelectField
            name={"timezone"}
            className={styles.timezone}
            isSearchable={true}
            isClearable={true}
            tooltip="Excludes DST."
          />
          <SelectField
            label={"Time Format"}
            tooltip={"Select the time format that will be shown for events created on Status Page."}
            name={"time_format"}
            className={styles.timeFormat}
            options={Object.keys(StatusPageTimeFormat).map((k) => ({
              value: k,
              label: StatusPageTimeFormatDisplay[k],
            }))}
          />
        </div>
        <FormSubtext subText={"Date/time for all Status Page events. Defaults to your account timezone."} />
      </div>

      {isEdit && (
        <>
          <div className={"d-flex flex-column gap-1"}>
            <FormLabel
              forId={"uptime_calculation_type"}
              helpText={
                "How global uptime is calculated. The uptime percentage of check-based components, or Incident-duration."
              }
            >
              Uptime Calculation
            </FormLabel>
            <div>
              <ToggleButton
                items={uptimeCalculationItems}
                selectedId={uptimeCalculationType || UptimeCalculationType.BY_INCIDENTS}
                onChange={(id) => setValue("uptime_calculation_type", id, {shouldDirty: true})}
              />
            </div>
          </div>

          <Section title={"advanced settings"} description={getAdvancedSettingsDescription()} defaultOpen={false}>
            <div className={"d-flex flex-column gap-4"}>
              {showAccessControls && (
                <>
                  {(isPublic || isPrivate) && (
                    <>
                      <Card variant={"white"} className={"d-flex flex-column gap-3 p-3 pb-4"}>
                        {isPublic && (
                          <>
                            <SwitchField
                              labelPosition={"left"}
                              name={"password_protection_enabled"}
                              label={"Password Protection"}
                              description={"Require visitors to enter credentials before viewing the Status Page"}
                            />
                            {watch("password_protection_enabled") && (
                              <>
                                <Divider />
                                <div className={"d-flex flex-column gap-3 ps-3 pb-2"}>
                                  <InputField
                                    name={"auth_username"}
                                    label={"Username"}
                                    helpText={"Secure this page using HTTP Basic Auth with this username"}
                                    labelTypography={"bodySmall"}
                                  />
                                  <PasswordField
                                    name={"auth_password"}
                                    label={"Password"}
                                    tooltip={
                                      "OPTIONAL: Secure this status page using HTTP Basic Auth with this password."
                                    }
                                    labelTypography={"bodySmall"}
                                  />
                                </div>
                                <Divider />
                              </>
                            )}
                          </>
                        )}
                        <SwitchField
                          labelPosition={"left"}
                          name={"allow_search_indexing"}
                          label={"Allow search engine indexing"}
                          description={"Allow Search Engines to Index your Status Page"}
                        />
                        <Divider />
                        <SwitchField
                          labelPosition={"left"}
                          name={"allow_drill_down"}
                          label={"Allow visitor drill down into individual checks"}
                          description={"Visitors may view performance graphs and reports of Check based components"}
                        />
                        {allowDrillDown && (
                          <>
                            <Divider />

                            <div className={"d-flex flex-column gap-3 ps-3 pb-2"}>
                              <InputField
                                name={"max_visible_component_days"}
                                label={"Drill-down maximum time period (in days)"}
                                tooltip={
                                  "Defines widest range of time users can view on check drill-down. When left empty this is Unlimited."
                                }
                                placeholder={"Unlimited (default)"}
                                type={"number"}
                                labelTypography={"bodySmall"}
                                className={"maxWidthInputs"}
                              />
                            </div>
                          </>
                        )}
                      </Card>
                    </>
                  )}

                  <InputField
                    name={"cname"}
                    label={"CNAME"}
                    tooltip={
                      userHasFeature("statuspages-cname")
                        ? "OPTIONAL: Create a CNAME record of your choice in your DNS and pointing it to cname.uptime.com."
                        : "CNAME support is coming, please contact support to prioritize it"
                    }
                    disabled={!userHasFeature("statuspages-cname")}
                    helpText={"Point your domain's CNAME record to cname.uptime.com."}
                    supportLink={
                      "https://support.uptime.com/hc/en-us/articles/360016256820-Setup-Instructions-for-your-Uptime-com-Status-Page#custom_CNAME"
                    }
                    supportLinkText={"View documentation"}
                  />
                </>
              )}

              <InputField
                name={"google_analytics_code"}
                label={"Google Analytics Property ID"}
                tooltip={"Your google analytics property ID."}
                placeholder={"Property ID such as UA-12345678-1"}
                className={"maxWidthInputs"}
              />
            </div>
          </Section>
        </>
      )}
    </div>
  );
};
